const MAX_SCALE = 16;

// Satellite imagery stops at a native zoom that depends on the area (often 18 on rural fields).
// Past it, the map content is scaled with CSS and dragged by hand.
// ponytail: Google still computes click positions on the unscaled map, so clicking the map itself is off while scaled.
export function addExtraZoom(map: google.maps.Map<HTMLDivElement>) {
  const container = map.getDiv();
  const maxZoomService = new google.maps.MaxZoomService();
  let nativeMaxZoom = 20;
  let scale = 1;
  let gestureHandling: google.maps.MapOptions['gestureHandling'] = 'auto';
  let lastPointer: { x: number; y: number } | null = null;

  const setScale = (value: number) => {
    if (scale === 1 && value > 1) {
      gestureHandling = map.get('gestureHandling') ?? 'auto';
      map.setOptions({ gestureHandling: 'none' });
    } else if (scale > 1 && value === 1) {
      map.setOptions({ gestureHandling });
    }
    scale = value;
    // Scales the tiles and overlays but not the controls, relies on the Google Maps DOM.
    const content = container.querySelector<HTMLElement>('.gm-style > div:first-child');
    if (content) content.style.transform = scale === 1 ? '' : `scale(${scale})`;
  };

  // Moves the center by unscaled map pixels.
  const shift = (dx: number, dy: number) => {
    const projection = map.getProjection();
    const center = map.getCenter();
    const zoom = map.getZoom();
    if (!projection || !center || zoom === undefined) return;
    const point = projection.fromLatLngToPoint(center);
    if (!point) return;
    const ratio = Math.pow(2, zoom);
    const next = projection.fromPointToLatLng(new google.maps.Point(point.x + dx / ratio, point.y + dy / ratio));
    if (next) map.setCenter(next);
  };

  container.addEventListener(
    'wheel',
    (event: WheelEvent) => {
      if (scale === 1 && (event.deltaY > 0 || (map.getZoom() ?? 0) < nativeMaxZoom)) return;
      event.preventDefault();
      event.stopPropagation();
      const delta = event.deltaMode === WheelEvent.DOM_DELTA_LINE ? event.deltaY * 33 : event.deltaY;
      const next = Math.min(MAX_SCALE, Math.max(1, scale * Math.pow(2, -delta / 200)));
      // Keeps the point under the cursor in place.
      const rect = container.getBoundingClientRect();
      const factor = 1 / scale - 1 / next;
      shift((event.clientX - rect.left - rect.width / 2) * factor, (event.clientY - rect.top - rect.height / 2) * factor);
      setScale(next);
    },
    { capture: true, passive: false }
  );

  container.addEventListener('pointerdown', (event: PointerEvent) => {
    if (scale > 1) lastPointer = { x: event.clientX, y: event.clientY };
  });
  container.addEventListener('pointermove', (event: PointerEvent) => {
    if (!lastPointer) return;
    shift((lastPointer.x - event.clientX) / scale, (lastPointer.y - event.clientY) / scale);
    lastPointer = { x: event.clientX, y: event.clientY };
  });
  const stopDrag = () => (lastPointer = null);
  container.addEventListener('pointerup', stopDrag);
  container.addEventListener('pointerleave', stopDrag);

  // This code never changes the zoom, so any zoom change (fitBounds on a new mission, field or path) resets the scale.
  map.addListener('zoom_changed', () => setScale(1));

  map.addListener('idle', () => {
    const center = map.getCenter();
    if (scale > 1 || !center || (map.getZoom() ?? 0) < 17) return;
    maxZoomService.getMaxZoomAtLatLng(center, (result) => {
      if (result.status === google.maps.MaxZoomStatus.OK) nativeMaxZoom = result.zoom;
    });
  });
}
